import React from 'react';
import Localize from 'react-intl-universal';
import { useNavigate } from 'react-router-dom';

import { alpha } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { styled } from '@mui/material/styles';

import CancelButton from './CancelButton';

const StyledBoxShadow = styled(Box)(({ theme }) => ({
  boxShadow: '4px 0 4px rgba(0,0,0,0.12)',
  width: '100% ',
  backgroundColor: `${theme.palette.primary.background} !important`,
  padding: '1rem 0 1rem 0',
  zIndex: '2',
  position: 'fixed',
  bottom: '5rem',
  left: '0',
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    bottom: '0'
  }
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    justifyContent: 'space-between'
  },
  [theme.breakpoints.up('lg')]: {
    paddingLeft: '7rem',
    paddingRight: '7rem'
  }
}));

const StyledBookingButton = styled(Button)(({ theme }) => ({
  minWidth: '10rem !important',
  textTransform: 'none !important',
  fontWeight: '700 !important',
  fontSize: '14px !important',
  height: '3rem',
  alignSelf: 'center',
  [theme.breakpoints.up('sm')]: {
    height: 'auto'
  }
}));

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'lastStep'
})(({ theme, lastStep }) => ({
  minWidth: '10rem',
  textTransform: 'none',
  fontWeight: '700',
  fontSize: '14px',
  backgroundColor: theme.palette?.action?.light,
  color: theme.palette.secondary.contrastText,
  display: lastStep ? 'none' : 'inline',
  [theme.breakpoints.up('lg')]: {
    '&:hover': {
      backgroundColor: alpha(theme.palette?.action?.main, 0.8),
      boxShadow: 'none'
    }
  }
}));

const BottomBox = ({
  isLoading,
  setActiveStep,
  activeStep,
  numberOfSteps,
  forLink: { goBack = '/' },
  isFormValid = true,
  redirectFrom,
  redirectPath
}) => {
  let navigate = useNavigate();

  const redirectToDetails = () => {
    navigate(goBack, {
      state: {
        from: Localize.get(redirectFrom),
        path: redirectPath
      }
    });
  };

  const handleNext = () => {
    if (activeStep < numberOfSteps && isFormValid) {
      setActiveStep((prevStep) => prevStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      return redirectToDetails();
    }
    if (activeStep > 0 && activeStep <= 6) {
      setActiveStep((prevStep) => prevStep - 1);
    }
    window.scrollTo(0, 0);
  };

  return (
    <>
      <StyledBoxShadow>
        <StyledContainer maxWidth='false'>
          <CancelButton
            goBack={goBack}
            redirectFrom={redirectFrom}
            redirectPath={redirectPath}
            sx={{ display: { xs: 'none', md: 'block' } }}
          />
          <Box sx={{ display: { xs: 'flex', sm: 'block' } }}>
            <StyledBookingButton
              disabled={isLoading}
              sx={{ color: (theme) => theme.palette.primary.main, mr: '2px' }}
              onClick={handleBack}
            >
              {Localize.get('App.back')}
            </StyledBookingButton>
            <StyledButton
              variant='contained'
              onClick={handleNext}
              disabled={!isFormValid || isLoading}
              lastStep={activeStep === numberOfSteps - 1}
            >
              {Localize.get('App.next')}
            </StyledButton>
          </Box>
        </StyledContainer>
      </StyledBoxShadow>
    </>
  );
};

export default BottomBox;
